import React, { useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Home, Building2, Landmark } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const IMG = (path) => `https://www.kinetico.com${path}?width=1200&height=1200`;

const RESIDENTIAL = [
  {
    tag: "Signature Series",
    name: "Kinetico S650 Signature",
    desc: "Non-electric twin-tank softener — Kinetico's proven Signature Series, sized for growing families.",
    img: IMG("/media/rqvijutr/935sig_fixedimage.png"),
  },
  {
    tag: "Drinking Water",
    name: "K5 Drinking Water Station",
    desc: "Kinetico's best reverse-osmosis system with configurable filtration.",
    img: IMG("/media/bjjkxtkr/img-k5-drinking-water-station-24.png"),
  },
  {
    tag: "Premier XP",
    name: "Kinetico Q950 Premier",
    desc: "Highest-flow twin-tank softener for larger homes and higher demand.",
    img: IMG("/media/whbcz3iz/q850-desaturated-sump.png"),
  },
  {
    tag: "Compact Softener",
    name: "Kinetico S150 Series",
    desc: "Non-electric twin-tank engineering in a compact footprint for tighter spaces.",
    img: IMG("/media/ulupujoc/s150xp_fixedimage.png"),
  },
  {
    tag: "Tankless RO",
    name: "Kinetico HYDRO® ECO™",
    desc: "Next-generation tankless RO with smart LED display and leak detection.",
    img: IMG("/media/222ih3om/hydro-eco.png"),
  },
  {
    tag: "Iron & Sulfur",
    name: "Powerline™ PRO Series",
    desc: "Electric single-tank filters that protect appliances from iron and sulfur.",
    img: IMG("/media/kirl15mb/imgpowerlineprosvfilter.png"),
  },
  {
    tag: "Sulfur Removal",
    name: "Sulfur Guard™ Filter",
    desc: "Effective, easy removal of hydrogen sulfide (rotten-egg smell) from water.",
    img: IMG("/media/plzbvo4s/sulfur-guard_resized.png"),
  },
  {
    tag: "Chlorine",
    name: "Dechlorinator",
    desc: "Single-tank whole-home chlorine removal — pairs perfectly with a softener.",
    img: IMG("/media/zx2opwgb/1030_dechlor_500x500.png"),
  },
  {
    tag: "Carbon Filter",
    name: "Carbon Backwashing Filter",
    desc: "Twin-tank coconut-shell carbon media for whole-house chlorine and taste reduction.",
    img: IMG("/media/3bqdfip4/carbonbackwashing2030s_500x500.png"),
  },
  {
    tag: "Chloramine",
    name: "Chloramine Reduction System",
    desc: "Eliminates the taste, odor and damaging effects of chloramines in city water.",
    img: IMG("/media/atzff5bj/1060_1100-crs.png"),
  },
  {
    tag: "Turbidity",
    name: "Macrolite® Backwashing Filter",
    desc: "Twin-tank proprietary media that removes turbidity with virtually no maintenance.",
    img: IMG("/media/1oipojdi/2030s_500x500.png"),
  },
  {
    tag: "Acidic Water",
    name: "Calcite Backwashing Filter",
    desc: "Dual-tank system that filters turbidity and raises pH to prevent corrosion.",
    img: IMG("/media/th4lhes3/calcite2030s_500x500.png"),
  },
  {
    tag: "pH Neutralizer",
    name: "Neutralizer",
    desc: "Single-tank system that balances pH and eliminates metallic taste from acidic water.",
    img: IMG("/media/rwhe0nq4/neutralizer1100_500x500.png"),
  },
  {
    tag: "RO Drinking",
    name: "AquaKinetic® A200",
    desc: "Reliable, entry-level RO drinking-water system.",
    img: IMG("/media/xjppm5nd/a200-faucet-tank_500x500.png"),
  },
];

const UNSPLASH = (id) => `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&w=1000&q=72&auto=format`;

const COMMERCIAL = [
  {
    tag: "Restaurants & Hotels",
    name: "Commercial Softeners",
    desc: "CP-series twin-tank softeners for restaurants, hotels, salons and multi-unit properties. Continuous soft water, non-electric.",
    photo: UNSPLASH("1414235077428-338989a2e8c0"),
  },
  {
    tag: "Foodservice",
    name: "Ice & Espresso Filtration",
    desc: "Point-of-use filtration for consistent taste, longer equipment life and cleaner ice — from coffee bars to full kitchens.",
    photo: UNSPLASH("1495474472287-4d71bcdd2085"),
  },
  {
    tag: "Healthcare & Labs",
    name: "Industrial RO & Deionizers",
    desc: "Ultra-pure water for laboratories, healthcare, dialysis clinics, electronics, spot-free rinse and manufacturing lines.",
    photo: UNSPLASH("1532187863486-abf9dbad1b69"),
  },
  {
    tag: "Salons & Spas",
    name: "Whole-Building Filtration",
    desc: "Sediment, chlorine and specialty filtration engineered for salons, spas, offices, gyms and hospitality facilities.",
    photo: UNSPLASH("1600334129128-685c5582fd35"),
  },
  {
    tag: "Manufacturing",
    name: "Process Water Treatment",
    desc: "Softeners, RO and specialty media sized for boilers, cooling towers, food processing and industrial applications.",
    photo: UNSPLASH("1616996691973-0560486764f7"),
  },
];

const MUNICIPAL = [
  {
    tag: "Municipal Filtration",
    name: "Macrolite® Municipal Systems",
    desc: "Proven filtration media used by water districts across the country to reduce turbidity and pathogens at scale.",
    photo: UNSPLASH("1581093588401-fbb62a02f120"),
  },
  {
    tag: "Well Fields",
    name: "Iron & Manganese Removal",
    desc: "High-capacity removal systems for community wells, small municipal supplies and rural water associations.",
    photo: UNSPLASH("1519455953755-af066f52f1a6"),
  },
  {
    tag: "Arsenic Reduction",
    name: "Arsenic Guard™ Industrial",
    desc: "Certified arsenic reduction sized for public water supplies, HOAs and community-scale wells.",
    photo: "https://images.pexels.com/photos/4108706/pexels-photo-4108706.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    tag: "Disinfection",
    name: "Chlorine & Chloramine Dosing",
    desc: "Precision chemical feed systems for municipal disinfection, corrosion control and residual management.",
    photo: "https://images.pexels.com/photos/4108710/pexels-photo-4108710.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    tag: "Wastewater",
    name: "Neutralization & pH Control",
    desc: "Balance discharge water to environmental standards for treatment plants and processing facilities.",
    photo: "https://images.pexels.com/photos/5691627/pexels-photo-5691627.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    tag: "Engineered Design",
    name: "Custom Treatment Trains",
    desc: "Custom-designed systems for towns, HOAs, agricultural operations, dairies and specialty industries.",
    photo: UNSPLASH("1521207418485-99c705420785"),
  },
];

const CATEGORIES = [
  { key: "residential", label: "Residential", Icon: Home, items: RESIDENTIAL, count: RESIDENTIAL.length },
  { key: "commercial", label: "Commercial", Icon: Building2, items: COMMERCIAL, count: COMMERCIAL.length },
  { key: "municipal", label: "Municipal", Icon: Landmark, items: MUNICIPAL, count: MUNICIPAL.length },
];

function ProductTile({ item, i }) {
  const isPhoto = !!item.img;
  const isLifestyle = !!item.photo;
  return (
    <Reveal delay={(i % 8) * 0.04} testId={`catalog-tile-${i}`}>
      <div className="h-full flex flex-col">
        <div
          className="relative overflow-hidden aspect-square border border-black/10"
          style={{ background: isPhoto ? "#F4F7FB" : "#071824" }}
        >
          {isPhoto && (
            <>
              <div
                aria-hidden
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[65%] rounded-full"
                style={{
                  background:
                    "radial-gradient(ellipse, rgba(95,179,221,0.28) 0%, transparent 70%)",
                  filter: "blur(30px)",
                }}
              />
              <img
                src={item.img}
                alt={item.name}
                loading="lazy"
                style={{ filter: "drop-shadow(0 20px 30px rgba(10,30,60,0.2))" }}
                className="absolute inset-0 w-full h-full object-contain p-6 lg:p-8"
              />
            </>
          )}
          {isLifestyle && (
            <>
              <img
                src={item.photo}
                alt={item.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(4,15,20,0.10) 0%, rgba(4,15,20,0.35) 55%, rgba(4,15,20,0.85) 100%)",
                }}
              />
            </>
          )}
          <div className={`absolute inset-0 ring-1 ring-inset ${isPhoto ? "ring-black/5" : "ring-white/10"}`} />
          <div
            className={`absolute top-4 left-4 text-[9px] uppercase tracking-[0.3em] font-medium ${
              isPhoto ? "text-[#0a3a80]" : "text-[color:var(--blue)]"
            }`}
          >
            {item.tag}
          </div>
        </div>
        <div className="mt-5 flex-1 flex flex-col">
          <h3 className="font-display font-semibold text-lg lg:text-xl text-white leading-tight">
            {item.name}
          </h3>
          <p className="mt-3 text-[13.5px] leading-[1.65] text-[color:var(--muted)] font-body flex-1">
            {item.desc}
          </p>
        </div>
      </div>
    </Reveal>
  );
}

export default function ProductCatalog() {
  const [active, setActive] = useState("residential");
  const activeCat = CATEGORIES.find((c) => c.key === active);

  return (
    <section
      className="relative py-32 lg:py-40 px-6 lg:px-10"
      data-testid="product-catalog"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 mb-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.35em] text-[color:var(--blue)]">
              Full Catalog
            </div>
            <Reveal>
              <h2 className="mt-4 font-display font-semibold text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] text-white">
                The Kinetico<br />
                <span className="font-editorial italic font-normal text-[color:var(--blue)]">
                  lineup.
                </span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <Reveal delay={0.15}>
              <p className="text-[15px] leading-[1.75] text-[color:var(--muted)] font-body max-w-2xl">
                From compact residential systems to municipal-scale treatment,
                Kinetico builds equipment for every water problem. Vanguard installs
                and services every category across Northern Utah and Southern Idaho.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 lg:gap-3 border-b border-[color:var(--border)] pb-6 mb-16"
          role="tablist"
          data-testid="catalog-tabs"
        >
          {CATEGORIES.map((c) => {
            const Icon = c.Icon;
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(c.key)}
                data-testid={`catalog-tab-${c.key}`}
                className="group relative inline-flex items-center gap-3 px-5 lg:px-6 py-3 lg:py-3.5 rounded-full border transition-colors duration-300"
                style={{
                  borderColor: isActive ? "var(--blue)" : "rgba(255,255,255,0.10)",
                  background: isActive ? "rgba(95,179,221,0.10)" : "transparent",
                }}
              >
                <Icon
                  className="h-4 w-4 transition-colors"
                  style={{ color: isActive ? "var(--blue)" : "rgba(255,255,255,0.55)" }}
                />
                <span
                  className="font-body text-[13px] tracking-wide transition-colors"
                  style={{ color: isActive ? "#fff" : "rgba(255,255,255,0.72)" }}
                >
                  {c.label}
                </span>
                <span
                  className="text-[10px] tabular tracking-widest transition-colors"
                  style={{ color: isActive ? "var(--blue)" : "rgba(255,255,255,0.35)" }}
                >
                  {String(c.count).padStart(2, "0")}
                </span>
              </button>
            );
          })}
        </div>

        {/* Product grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-10"
            data-testid={`catalog-grid-${active}`}
          >
            {activeCat.items.map((item, i) => (
              <ProductTile key={item.name} item={item} i={i} />
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-20 pt-10 border-t border-[color:var(--border)] flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="max-w-md text-[14px] leading-[1.7] text-[color:var(--muted)] font-body">
            Don't see what you need? Vanguard sizes and installs custom Kinetico
            systems for any application. Talk to a specialist.
          </p>
          <Link to="/contact" className="btn-primary" data-testid="catalog-cta">
            Talk to a Specialist <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
